import React, { useMemo, useState } from 'react'
import { Box, Divider, IconButton, Typography } from '@mui/material'
import type { ColumnDef } from '@tanstack/react-table'
import { TanStackTable } from '@/@core/components/mui/TanStackTable'
import { TablePagination, TablePaginationProps } from '@/@core/components/mui/TablePagination'
import { IUser, useExportEmployeeMutation, useUpdateUserStatusMutation } from '@/@data'
import dayjs from 'dayjs'
import { UserAvatar } from '../common/UserAvatar'
import UserStatusDropDown from '../user/UserStatusDropDown'
import { useNotification } from '@/hooks/useNotification'
import { Button } from '@/@core/components/mui/Button'
import { useRowSelection } from '@/@core/hooks'
import { useRouteQuery } from '@/hooks/useRouteQuery'
import { EmployeeDetailDialog } from '../user/employee/EmployeeDetailDialog'

interface EmployerDetailTableProps {
  data: IUser[]
  meta: unknown
  onRowClick?: (employee: IUser) => void
  onEdit?: (employee: IUser) => void
  refetch: () => void
  isDetail?: boolean
}

export const EmployerDetailTable: React.FC<EmployerDetailTableProps> = ({
  data,
  meta,
  onEdit,
  refetch,
  isDetail = false
}) => {
  const { __ } = useTranslator()
  const { rowSelection, setRowSelection } = useRowSelection(data)
  const changeStatusMutation = useUpdateUserStatusMutation()
  const n = useNotification()
  const { searchQuery } = useRouteQuery()
  const [open, setOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<IUser | null>(null)
  const exportEmployeeMutation = useExportEmployeeMutation()

  const columns = useMemo<ColumnDef<IUser>[]>(
    () => [
      {
        accessorKey: 'employee',
        header: __('Employees'),
        size: 200,
        cell: ({ row }) => {
          return <UserAvatar user={row.original} link={`/cms/users/employee/${row.original.id}?month=${row.original?.company?.current_pay_period}`} />
        }
      },
      {
        accessorKey: 'employee_id',
        header: __('Employee ID'),
        size: 180,
        cell: ({ row }) => {
          return (
            <Typography variant='body2' color='text.primary' className='text-[15px] font-normal text-[#2f2b3db3]'>
              {row.original.employee_id || '-'}
            </Typography>
          )
        }
      },
      {
        accessorKey: 'tenure',
        header: __('Tenure'),
        size: 150,
        cell: ({ row }) => (
          <Typography variant='body2' color='text.primary' className='text-[15px] font-normal text-[#2f2b3de6]'>
            {row.original?.joined_date
              ? `${dayjs().diff(dayjs(row.original?.joined_date), 'month')} ${__('months')}`
              : row.original?.tenure || '-'}
          </Typography>
        )
      },
      {
        accessorKey: 'birth_date',
        header: __('D.O.B'),
        size: 150,
        cell: ({ row }) => (
          <Typography variant='body2' color='text.primary' className='text-[15px] font-normal text-[#2f2b3db3]'>
            {row.original.birth_date ? dayjs(row.original.birth_date).format('DD/MM/YYYY') : '-'}
          </Typography>
        )
      },
      {
        accessorKey: 'status',
        header: __('Status'),
        size: 150,
        cell: ({ row }) => {
          const employee = row.original

          return (
            <UserStatusDropDown
              status={employee.status}
              statusPrevious={employee.status_previous}
              isDetail={isDetail}
              onSelect={status => {
                changeStatusMutation.mutate(
                  {
                    id: [row.original.id],
                    status
                  },
                  {
                    onSuccess: () => {
                      refetch()
                      n.success(__('Status updated successfully'))
                    },
                    onError: () => {
                      n.error(__('Status update failed'))
                    }
                  }
                )
              }}
            />
          )
        }
      },
      {
        accessorKey: 'actions',
        header: __('Actions'),
        size: 150,
        cell: ({ row }) => {
          return (
            <IconButton
              onClick={() => {
                setSelectedEmployee(row.original)
                setOpen(true)
              }}
              size='small'
            >
              <i className='tabler-eye text-[#2F2B3DB2] size-[20px]' />
            </IconButton>
          )
        }
      }
    ],
    [onEdit]
  )

  return (
    <>
      {Object.keys(rowSelection).length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px'
            }}
          >
            <Box>
              <Button variant='outlined' color='secondary' className='mr-2'>
                {Object.keys(rowSelection).length} {__('Selected')}
              </Button>
            </Box>

            <Box className='flex items-center gap-2'>
              <Button
                variant='tonal'
                color='primary'
                onClick={() => {
                  const selectedUserIds = Object.keys(rowSelection).map(Number)

                  exportEmployeeMutation.mutate({ userIds: selectedUserIds }, {
                    onSuccess: () => {
                      n.success(__('Employee data exported successfully'))
                    },
                    onError: () => {
                      n.error(__('Failed to export employee data'))
                    }
                  })
                }}
                disabled={exportEmployeeMutation.isPending}
                startIcon={<i className='tabler-upload' />}
              >
                {exportEmployeeMutation.isPending ? __('Exporting...') : __('Export Employee')}
              </Button>
              <Button
                variant='tonal'
                color='secondary'
                onClick={() => setRowSelection({})}
                startIcon={<i className='tabler-x' />}
              >
                {__('Clear')}
              </Button>
            </Box>
          </Box>
        </>
      )}

      <TanStackTable
        columns={columns}
        tableData={data}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        enableRowSelection
        borderRight='2px solid #e0e0e0'
      />

      {meta && (
        <TablePagination
          meta={
            {
              ...meta,
              page: Number(searchQuery.get('page')) || 1
            } as unknown as TablePaginationProps
          }
        />
      )}
      <EmployeeDetailDialog
        open={open}
        onClose={() => {
          setOpen(false)
          setSelectedEmployee(null)
        }}
        employee={selectedEmployee}
        refetch={refetch}
      />
    </>
  )
}
